import React, {useState} from "react";
import {Link} from "react-router-dom";
import {FiCamera, FiChevronDown, FiChevronUp, FiExternalLink, FiHome, FiImage, FiLogIn, FiLogOut} from "react-icons/fi";
import ETable, {EmptyState} from "../components/Table";
import {fmtDateTime} from "../../../functions/formatDate";

// ─── config maps ─────────────────────────────────────────────────────────────

const RESULT_CFG = {
    ENTRY_SUCCESS:                {label: "Vchod OK",               color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0"},
    EXIT_SUCCESS:                 {label: "Východ OK",              color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0"},
    ENTRY_IN_AREA:                {label: "Průchod v areálu OK",    color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0"},
    OK:                           {label: "OK",                     color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0"},
    TICKET_BLOCATION_OK:          {label: "Blokace OK",             color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0"},
    IMAGE_GET_SUCCESS:            {label: "Fotka získána",          color: "#2563eb", bg: "#eff6ff", border: "#bfdbfe"},
    NOT_IN_ZONE:                  {label: "Ještě nevstoupil",       color: "#d97706", bg: "#fffbeb", border: "#fde68a"},
    NOT_FOUND:                    {label: "Nenalezena",             color: "#dc2626", bg: "#fef2f2", border: "#fecaca"},
    IN_ZONE:                      {label: "Již vstoupil",           color: "#dc2626", bg: "#fef2f2", border: "#fecaca"},
    CARD_USED:                    {label: "Vstupenka vyčerpána",    color: "#dc2626", bg: "#fef2f2", border: "#fecaca"},
    INCORRECT_ZONE:               {label: "Špatný vchod",           color: "#dc2626", bg: "#fef2f2", border: "#fecaca"},
    UNKNOWN_TURNSTILE:            {label: "Neznámý turniket",       color: "#dc2626", bg: "#fef2f2", border: "#fecaca"},
    NOT_IN_PARENT_ZONE:           {label: "Není v nadřaz. zóně",    color: "#dc2626", bg: "#fef2f2", border: "#fecaca"},
    FULL_CAPACITY:                {label: "Plná kapacita",          color: "#dc2626", bg: "#fef2f2", border: "#fecaca"},
    NO_TICKET_ENTRANCE_BLOCATION: {label: "Bez blokace vchodu",     color: "#dc2626", bg: "#fef2f2", border: "#fecaca"},
    TICKET_ENTRANCE_BLOCKED:      {label: "Vchod zablokován",       color: "#dc2626", bg: "#fef2f2", border: "#fecaca"},
    NO_CREDIT:                    {label: "Nedostatek kreditu",     color: "#dc2626", bg: "#fef2f2", border: "#fecaca"},
    READING_BLOCKED:              {label: "Čtení blokováno",        color: "#dc2626", bg: "#fef2f2", border: "#fecaca"},
    FACE_NOT_RECOGNIZED:          {label: "Obličej nerozpoznán",    color: "#dc2626", bg: "#fef2f2", border: "#fecaca"},
};

const ENTRY_TYPE_CFG = {
    0: {label: "Východ",       Icon: FiLogOut,  color: "#dc2626", bg: "#fef2f2", border: "#fecaca"},
    1: {label: "Vchod",        Icon: FiLogIn,   color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0"},
    2: {label: "V areálu",     Icon: FiHome,    color: "#2563eb", bg: "#eff6ff", border: "#bfdbfe"},
    3: {label: "Přiřaz. foto", Icon: FiCamera,  color: "#7c3aed", bg: "#f5f3ff", border: "#ddd6fe"},
    4: {label: "Získání foto", Icon: FiImage,   color: "#0891b2", bg: "#ecfeff", border: "#a5f3fc"},
};

const SOURCE_CFG = {
    entrance:      {label: "Průchod",      color: "#2563eb", bg: "#eff6ff", border: "#bfdbfe"},
    entrance_log:  {label: "Log průchodu", color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0"},
    log:           {label: "Log",          color: "#64748b", bg: "#f8fafc", border: "#e2e8f0"},
};

const TICKET_TYPE_LABEL = {0: "Servisní karta", 1: "Běžná vstupenka", 2: "Permanentka / kredit"};
const DISCOUNT_LABEL    = {1: "Dětské", 2: "Zlevněné"};

// ─── sub-components ───────────────────────────────────────────────────────────

const Badge = ({cfg, fallback}) => {
    if (!cfg) return <span style={{fontSize: "var(--enigoo-fs-xs)", color: "#94a3b8"}}>{fallback || "—"}</span>;
    const Icon = cfg.Icon;
    return (
        <span style={{
            display: "inline-flex", alignItems: "center", gap: "4px",
            fontSize: "var(--enigoo-fs-xs)", fontWeight: 600,
            color: cfg.color, background: cfg.bg, border: `1px solid ${cfg.border}`,
            borderRadius: "5px", padding: "2px 7px", whiteSpace: "nowrap",
        }}>
            {Icon && <Icon size={11}/>}
            {cfg.label}
        </span>
    );
};

const DetailRow = ({label, value}) => {
    if (value == null || value === "" || value === "-") return null;
    return (
        <div style={{display: "flex", gap: "8px", alignItems: "baseline"}}>
            <span style={{fontSize: "var(--enigoo-fs-2xs)", color: "#94a3b8", minWidth: "110px", flexShrink: 0}}>{label}</span>
            <span style={{fontSize: "var(--enigoo-fs-xs)", color: "#374151", fontWeight: 500}}>{String(value)}</span>
        </div>
    );
};

const DetailSection = ({title, children}) => (
    <div>
        <p style={{fontSize: "var(--enigoo-fs-2xs)", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em", margin: "0 0 5px 0"}}>{title}</p>
        <div style={{display: "flex", flexDirection: "column", gap: "3px"}}>{children}</div>
    </div>
);

const td = (extra = {}) => ({padding: "8px 10px", fontSize: "var(--enigoo-fs-sm)", ...extra});

const EntranceRow = ({row, rowIndex}) => {
    const [expanded, setExpanded] = useState(false);
    const [hovered, setHovered]   = useState(false);

    const stripeBase = rowIndex % 2 === 0 ? "#fff" : "#f8fafc";
    const bg = expanded ? "#f0f9ff" : hovered ? "#eff6ff" : stripeBase;

    const resultCfg    = RESULT_CFG[row.result];
    const entryTypeCfg = ENTRY_TYPE_CFG[row.type];
    const sourceCfg    = SOURCE_CFG[row.sourceTable];

    return (
        <>
            <tr
                onClick={() => setExpanded(v => !v)}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                style={{background: bg, borderBottom: "1px solid #e2e8f0", cursor: "pointer"}}
            >
                <td style={td({color: "#475569", whiteSpace: "nowrap", fontFamily: "monospace"})}>{fmtDateTime(row.date)}</td>
                <td style={td({fontWeight: 700, color: "#1e293b", fontFamily: "monospace"})}>{row.bc || "—"}</td>
                <td style={td({color: "#374151"})}>{row.ticketName || <span style={{color: "#cbd5e1"}}>—</span>}</td>
                <td style={td()}><Badge cfg={entryTypeCfg} fallback="—"/></td>
                <td style={td()}><Badge cfg={resultCfg} fallback={row.result || "—"}/></td>
                <td style={td({color: "#475569"})}>
                    {row.turnstileDescription || row.turnstileIpAddress ? (
                        <>
                            {row.turnstileDescription && <span style={{fontWeight: 600, color: "#1e293b"}}>{row.turnstileDescription}</span>}
                            {row.turnstileIpAddress   && <span style={{color: "#94a3b8", fontFamily: "monospace", marginLeft: "4px"}}>({row.turnstileIpAddress})</span>}
                        </>
                    ) : <span style={{color: "#cbd5e1"}}>—</span>}
                </td>
                <td style={td({color: "#374151"})}>{row.zoneName   || <span style={{color: "#cbd5e1"}}>—</span>}</td>
                <td style={td({color: "#374151"})}>{row.tariffName || <span style={{color: "#cbd5e1"}}>—</span>}</td>
                <td style={td({textAlign: "right"})}>
                    {row.price != null
                        ? <span style={{fontWeight: 700, color: "#1e293b"}}>{row.price} Kč</span>
                        : <span style={{color: "#cbd5e1"}}>—</span>
                    }
                </td>
                <td style={td({color: "#94a3b8", textAlign: "center"})}>
                    {expanded ? <FiChevronUp size={13}/> : <FiChevronDown size={13}/>}
                </td>
            </tr>

            {expanded && (
                <tr style={{background: "#f0f9ff", borderBottom: "1px solid #e2e8f0"}}>
                    <td colSpan={10} style={{padding: "12px 16px"}}>
                        <div style={{display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: "16px"}}>
                            <DetailSection title="Vstupenka">
                                <DetailRow label="ID"              value={row.ticketId}/>
                                <DetailRow label="BC"              value={row.bc}/>
                                <DetailRow label="Jméno"           value={row.ticketName}/>
                                <DetailRow label="Poznámka"        value={row.ticketNote}/>
                                <DetailRow label="Typ"             value={TICKET_TYPE_LABEL[row.ticketTicketType]}/>
                                <DetailRow label="Sleva"           value={DISCOUNT_LABEL[row.ticketType]}/>
                                <DetailRow label="Platnost od"     value={fmtDateTime(row.ticketDateFrom)}/>
                                <DetailRow label="Platnost do"     value={fmtDateTime(row.ticketDateTo)}/>
                                <DetailRow label="Počet průchodů"  value={row.ticketEntrance}/>
                                <DetailRow label="Událost ID"      value={row.eventId}/>
                                {row.ticketId != null && (
                                    <Link
                                        to={"/ticket/detail/" + row.ticketId}
                                        onClick={(e) => e.stopPropagation()}
                                        style={{
                                            display: "inline-flex", alignItems: "center", gap: "5px",
                                            marginTop: "6px", fontSize: "var(--enigoo-fs-xs)", fontWeight: 600,
                                            color: "#2563eb", textDecoration: "none",
                                        }}
                                    >
                                        <FiExternalLink size={12}/>
                                        Detail vstupenky
                                    </Link>
                                )}
                            </DetailSection>

                            <DetailSection title="Turniket">
                                <DetailRow label="Popis"           value={row.turnstileDescription}/>
                                <DetailRow label="IP"              value={row.turnstileIpAddress}/>
                                <DetailRow label="MAC"             value={row.turnstileMacAddress}/>
                                <DetailRow label="Poslední akce"   value={row.turnstileLastAction}/>
                                <DetailRow label="Čas otevření"    value={row.turnstileOpenTime}/>
                            </DetailSection>

                            <DetailSection title="Tarif & Zóna">
                                <DetailRow label="Tarif"           value={row.tariffName}/>
                                <DetailRow label="Denní"           value={row.tariffDaily   != null ? (row.tariffDaily   ? "Ano" : "Ne") : null}/>
                                <DetailRow label="Reverzní"        value={row.tariffReverse != null ? (row.tariffReverse ? "Ano" : "Ne") : null}/>
                                <DetailRow label="Zóna"            value={row.zoneName}/>
                                <DetailRow label="Kapacita"        value={row.zoneCapacity}/>
                                <DetailRow label="Časový limit"    value={row.zoneTimeLimit ? `${row.zoneTimeLimit} min` : null}/>
                            </DetailSection>

                            <DetailSection title="Meta">
                                <DetailRow label="ID záznamu"      value={row.id}/>
                                <DetailRow label="Zdroj"           value={sourceCfg?.label ?? row.sourceTable}/>
                                <DetailRow label="Cena"            value={row.price != null ? `${row.price} Kč` : null}/>
                            </DetailSection>
                        </div>
                    </td>
                </tr>
            )}
        </>
    );
};

// ─── main ─────────────────────────────────────────────────────────────────────

const COLUMNS = [
    {header: "Datum"},
    {header: "BC"},
    {header: "Jméno"},
    {header: "Typ průchodu"},
    {header: "Výsledek"},
    {header: "Turniket"},
    {header: "Zóna"},
    {header: "Tarif"},
    {header: "Cena", center: true},
    {header: ""},
];

const EntranceTableV2 = ({data}) => (
    <ETable
        columns={COLUMNS}
        data={data}
        emptyText="Žádné záznamy"
        renderRow={(row, i) => <EntranceRow key={row.id ?? i} row={row} rowIndex={i}/>}
    />
);

export default EntranceTableV2;
